/**
 * The two row kinds of `AzurePickerTree`: a group item (subscription or resource
 * kind) whose `treeitem` element CONTAINS its `role="group"` of children (the
 * WAI-ARIA tree structure, C4A-05), with a clickable header (chevron, optional
 * tri-state checkbox, label + count); and a resource item (optional checkbox,
 * name, resource group · location or "already saved"). Selection convention:
 * multi mode exposes `aria-checked` (true / false / mixed) on every item, single
 * mode `aria-selected` on resources. The parent decides which item is the tab
 * stop and what a click / key does; the `azure-picker-row` test id sits on the
 * clickable header so a text filter matches only the row that carries the text.
 */
import type { FocusEvent, ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';

export type CheckedState = boolean | 'indeterminate';

const ITEM_CLASS = 'outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';
const ROW_CLASS = 'flex items-center gap-2 rounded-sm text-sm';

const ariaChecked = (checked: CheckedState): 'true' | 'false' | 'mixed' => (checked === 'indeterminate' ? 'mixed' : checked ? 'true' : 'false');

/** Focus events bubble in React; an item activates only when IT was focused, not a child of it. */
const ownFocus = (onActivate: () => void) => (event: FocusEvent<HTMLDivElement>) => {
  if (event.target === event.currentTarget) onActivate();
};

export function GroupRow(props: {
  rowKey: string;
  level: 1 | 2;
  open: boolean;
  label: string;
  count: number;
  multiSelect: boolean;
  checked: CheckedState;
  tabStop: boolean;
  inert: boolean;
  onActivate: () => void;
  onToggleOpen: () => void;
  onToggleChecked: (checked: boolean) => void;
  children?: ReactNode;
}) {
  const Chevron = props.open ? ChevronDown : ChevronRight;
  return (
    <div
      role="treeitem"
      aria-level={props.level}
      aria-expanded={props.open}
      aria-checked={props.multiSelect ? ariaChecked(props.checked) : undefined}
      tabIndex={props.tabStop ? 0 : -1}
      data-row-key={props.rowKey}
      onFocus={ownFocus(props.onActivate)}
      className={ITEM_CLASS}
    >
      <div
        data-testid="azure-picker-row"
        onClick={props.onToggleOpen}
        className={cn(ROW_CLASS, 'cursor-pointer px-1 py-1 hover:bg-accent/50')}
        style={{ paddingLeft: `${(props.level - 1) * 16 + 4}px` }}
      >
        <Chevron className="h-4 w-4 opacity-60" aria-hidden="true" />
        {props.multiSelect ? (
          <Checkbox
            checked={props.checked}
            disabled={props.inert}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(v) => props.onToggleChecked(v === true)}
            aria-label={`Select ${props.label}`}
          />
        ) : null}
        <span className="flex flex-1 items-center gap-2 text-left">
          <span className="font-medium">{props.label}</span>
          <span className="text-xs text-muted-foreground">{props.count}</span>
        </span>
      </div>
      {props.open && props.children ? <div role="group">{props.children}</div> : null}
    </div>
  );
}

export function ResourceRow(props: {
  resource: AzureDiscoveredResource;
  multiSelect: boolean;
  /** Entry mode: a double-click confirms the row in the multi-select tree too (C3.1). */
  confirmOnRow?: boolean;
  checked: boolean;
  focused: boolean;
  disabled: boolean;
  inert: boolean;
  tabStop: boolean;
  onActivate: () => void;
  onToggle: (checked: boolean) => void;
  onConfirm: () => void;
}) {
  const { resource, disabled, inert } = props;
  const unavailable = disabled || inert;
  return (
    <div
      role="treeitem"
      aria-level={3}
      aria-selected={props.multiSelect ? undefined : props.focused}
      aria-checked={props.multiSelect ? ariaChecked(props.checked) : undefined}
      aria-disabled={unavailable || undefined}
      tabIndex={props.tabStop ? 0 : -1}
      data-testid="azure-picker-row"
      data-row-key={resource.id}
      data-resource-id={resource.id}
      onFocus={ownFocus(props.onActivate)}
      onClick={unavailable ? undefined : props.onActivate}
      onDoubleClick={unavailable || (props.multiSelect && !props.confirmOnRow) ? undefined : props.onConfirm}
      className={cn(
        ITEM_CLASS,
        ROW_CLASS,
        'py-1 pr-2',
        unavailable ? 'opacity-50' : 'cursor-pointer hover:bg-accent/50',
        props.focused && !unavailable ? 'bg-accent' : ''
      )}
      style={{ paddingLeft: '52px' }}
    >
      {props.multiSelect ? (
        <Checkbox
          checked={props.checked}
          disabled={unavailable}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={(v) => props.onToggle(v === true)}
          aria-label={`Select ${resource.name}`}
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{resource.name}</span>
        <span className="truncate text-xs text-muted-foreground">
          {disabled ? 'already saved ✓' : `${resource.resourceGroup} · ${resource.location}`}
        </span>
      </div>
    </div>
  );
}
